// Procedural parallax fallback: 3 biomes × 5 layers of 2048×1080, horizontally
// seamless (everything that crosses an edge is drawn again shifted by ±W). Scenery
// has no outlines; depth reads through value and haze (far = lighter/hazier, near =
// darker, fore = near-black). AI-generated layers listed in the manifest load first
// and win, because newCanvas() skips keys that already exist.

import type Phaser from 'phaser';
import { PARALLAX_LAYERS, type ParallaxLayer } from '../../config/constants';
import { mix } from '../../config/palette';
import type { Rng } from '../../core/rng';
import { BIOME_IDS, type BiomeId } from '../../core/types';
import { parallaxKey } from '../keys';
import { TAU, newCanvas, rgba, seeded, softDot, softEllipse, type G } from './canvas';

const W = 2048;
const H = 1080;
/** Horizon line shared by every layer of a biome. */
const HORIZON = 760;

type Painter = (g: G, rng: Rng) => void;

export function buildParallax(scene: Phaser.Scene): void {
  for (const biome of BIOME_IDS) {
    for (const layer of PARALLAX_LAYERS) {
      const key = parallaxKey(biome, layer);
      const c = newCanvas(scene, key, W, H);
      if (!c) continue;
      PAINTERS[biome][layer](c.g, seeded(key));
      c.tex.refresh();
    }
  }
}

// ───────────────────────────── Helpers ─────────────────────────────

/** Draw at x and, when the shape crosses an edge, again at x ± W (seamless wrap). */
function wrap(g: G, x0: number, x1: number, draw: (dx: number) => void): void {
  draw(0);
  if (x0 < 0) draw(W);
  if (x1 > W) draw(-W);
}

function fill(g: G, color: number, alpha = 1): void {
  g.fillStyle = rgba(color, alpha);
}

/** Vertical gradient over the whole canvas width. */
function vgrad(g: G, y0: number, y1: number, stops: readonly [number, number, number?][]): void {
  const grad = g.createLinearGradient(0, y0, 0, y1);
  for (const [o, c, a] of stops) grad.addColorStop(o, rgba(c, a ?? 1));
  g.fillStyle = grad;
  g.fillRect(0, y0, W, y1 - y0);
}

/** Atmospheric haze over what is already drawn (keeps transparency): fades towards `color` lower down. */
function haze(g: G, color: number, y0: number, y1: number, a0: number, a1: number): void {
  g.save();
  g.globalCompositeOperation = 'source-atop';
  const grad = g.createLinearGradient(0, y0, 0, y1);
  grad.addColorStop(0, rgba(color, a0));
  grad.addColorStop(1, rgba(color, a1));
  g.fillStyle = grad;
  g.fillRect(0, Math.min(y0, y1), W, Math.abs(y1 - y0));
  if (y1 < H) {
    g.fillStyle = rgba(color, a1);
    g.fillRect(0, y1, W, H - y1);
  }
  g.restore();
}

/** Soft glow that wraps across the seam. */
function glow(g: G, x: number, y: number, r: number, color: number, alpha: number): void {
  wrap(g, x - r, x + r, (dx) => softDot(g, x + dx, y, r, color, alpha));
}

function glowEllipse(g: G, x: number, y: number, rx: number, ry: number, color: number, alpha: number): void {
  wrap(g, x - rx, x + rx, (dx) => softEllipse(g, x + dx, y, rx, ry, color, alpha));
}

type Crown = 'flat' | 'stepped' | 'spire' | 'slant' | 'broken' | 'dome';

/** Tower silhouette from `top` to the canvas bottom with a crown and optional mast. */
function tower(g: G, x: number, w: number, top: number, color: number, crown: Crown, mast = 0): void {
  wrap(g, x - 4, x + w + 4, (dx) => {
    const X = x + dx;
    fill(g, color);
    g.fillRect(X, top, w, H - top);
    switch (crown) {
      case 'stepped': {
        const h1 = w * 0.25;
        g.fillRect(X + w * 0.12, top - h1, w * 0.76, h1 + 1);
        g.fillRect(X + w * 0.3, top - h1 * 1.9, w * 0.4, h1 + 1);
        break;
      }
      case 'spire':
        g.beginPath();
        g.moveTo(X, top + 1);
        g.lineTo(X + w * 0.5, top - w * 0.9);
        g.lineTo(X + w, top + 1);
        g.fill();
        break;
      case 'slant':
        g.beginPath();
        g.moveTo(X, top + 1);
        g.lineTo(X, top - w * 0.45);
        g.lineTo(X + w, top + 1);
        g.fill();
        break;
      case 'broken': {
        g.beginPath();
        g.moveTo(X, top + 1);
        const n = 5;
        for (let i = 0; i <= n; i++) {
          const yy = top - ((i * 37 + Math.floor(x)) % 5) * (w * 0.06) - (i % 2 ? w * 0.14 : 0);
          g.lineTo(X + (w * i) / n, yy);
        }
        g.lineTo(X + w, top + 1);
        g.fill();
        break;
      }
      case 'dome':
        g.beginPath();
        g.ellipse(X + w / 2, top + 1, w / 2, w * 0.28, 0, Math.PI, TAU);
        g.fill();
        break;
      default:
        g.fillRect(X + w * 0.1, top - 8, w * 0.25, 9);
        g.fillRect(X + w * 0.55, top - 14, w * 0.3, 15);
    }
    if (mast > 0) {
      const mx = X + w * (crown === 'slant' ? 0.12 : 0.5);
      const base = crown === 'spire' ? top - w * 0.8 : crown === 'stepped' ? top - w * 0.45 : top - 10;
      g.fillRect(mx - 1.5, base - mast, 3, mast + 2);
      g.fillRect(mx - 6, base - mast * 0.55, 12, 2);
    }
  });
}

/** Sparse lit windows inside a rect. */
function windows(g: G, x: number, w: number, top: number, bottom: number, rng: Rng, colors: number[], density: number, alpha: number, size = 3): void {
  const cols = Math.max(1, Math.floor((w - 6) / (size * 3)));
  const rows = Math.max(1, Math.floor((bottom - top - 6) / (size * 3.2)));
  for (let r = 0; r < rows; r++) {
    // Whole floors light up together, which reads as architecture rather than noise.
    const floorLit = rng.chance(density * 3);
    for (let c = 0; c < cols; c++) {
      if (!floorLit || !rng.chance(0.45)) continue;
      const wx = x + 4 + c * size * 3;
      const wy = top + 6 + r * size * 3.2;
      const col = colors[rng.int(0, colors.length - 1)];
      const a = alpha * rng.float(0.45, 1);
      wrap(g, wx, wx + size * 2, (dx) => {
        fill(g, col, a);
        g.fillRect(wx + dx, wy, size * 2, size);
      });
    }
  }
}

/** Lattice girder between two points (chords + zig-zag bracing). */
function lattice(g: G, x0: number, y0: number, x1: number, y1: number, depth: number, color: number, lw = 3): void {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const ang = Math.atan2(y1 - y0, x1 - x0);
  const minX = Math.min(x0, x1) - depth;
  const maxX = Math.max(x0, x1) + depth;
  wrap(g, minX, maxX, (dx) => {
    g.save();
    g.translate(x0 + dx, y0);
    g.rotate(ang);
    g.strokeStyle = rgba(color);
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(0, -depth / 2);
    g.lineTo(len, -depth / 2);
    g.moveTo(0, depth / 2);
    g.lineTo(len, depth / 2);
    const step = depth * 1.1;
    for (let s = 0, k = 0; s < len; s += step, k++) {
      g.moveTo(s, k % 2 ? -depth / 2 : depth / 2);
      g.lineTo(Math.min(len, s + step), k % 2 ? depth / 2 : -depth / 2);
    }
    g.stroke();
    g.restore();
  });
}

/** Sagging cable between two points. */
function cable(g: G, x0: number, y0: number, x1: number, y1: number, sag: number, color: number, lw: number): void {
  wrap(g, Math.min(x0, x1), Math.max(x0, x1), (dx) => {
    g.strokeStyle = rgba(color);
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(x0 + dx, y0);
    g.quadraticCurveTo((x0 + x1) / 2 + dx, Math.max(y0, y1) + sag, x1 + dx, y1);
    g.stroke();
  });
}

function rect(g: G, x: number, y: number, w: number, h: number, color: number, alpha = 1): void {
  wrap(g, x, x + w, (dx) => {
    fill(g, color, alpha);
    g.fillRect(x + dx, y, w, h);
  });
}

/** Horizontal pipe run with flanges. */
function pipe(g: G, x: number, y: number, len: number, r: number, color: number, hi: number): void {
  rect(g, x, y - r, len, r * 2, color);
  rect(g, x, y - r, len, Math.max(1, r * 0.35), hi, 0.5);
  for (let s = x + 40; s < x + len - 10; s += 140) rect(g, s, y - r - 3, 8, r * 2 + 6, color);
}

/** Thin faded neon tube (scenery accents stay faint). */
function neonLine(g: G, x0: number, y0: number, x1: number, y1: number, color: number, alpha: number, w = 2): void {
  wrap(g, Math.min(x0, x1) - 8, Math.max(x0, x1) + 8, (dx) => {
    g.lineCap = 'round';
    g.strokeStyle = rgba(color, alpha * 0.25);
    g.lineWidth = w * 4;
    g.beginPath();
    g.moveTo(x0 + dx, y0);
    g.lineTo(x1 + dx, y1);
    g.stroke();
    g.strokeStyle = rgba(color, alpha);
    g.lineWidth = w;
    g.stroke();
  });
}

/** Abstract "dead billboard": frame with a dim panel and a broken neon border (no letters). */
function billboard(g: G, x: number, y: number, w: number, h: number, frame: number, panel: number, neon: number, rng: Rng, neonAlpha: number): void {
  rect(g, x + w * 0.2, y + h, 6, 60, frame);
  rect(g, x + w * 0.75, y + h, 6, 60, frame);
  rect(g, x - 4, y - 4, w + 8, h + 8, frame);
  rect(g, x, y, w, h, panel);
  // Abstract blocks on the panel (old ad shapes, faded).
  for (let i = 0; i < 3; i++) {
    const bw = rng.float(0.15, 0.4) * w;
    const bx = x + rng.float(0.05, 0.6) * w;
    const by = y + rng.float(0.15, 0.7) * h;
    rect(g, bx, by, bw, h * 0.12, neon, neonAlpha * 0.25);
  }
  // Broken neon border segments.
  const segs: [number, number, number, number][] = [
    [x + 3, y + 3, x + w - 3, y + 3],
    [x + w - 3, y + 3, x + w - 3, y + h - 3],
    [x + w - 3, y + h - 3, x + 3, y + h - 3],
    [x + 3, y + h - 3, x + 3, y + 3],
  ];
  for (const [ax, ay, bx, by] of segs) {
    if (rng.chance(0.3)) continue;
    const t0 = rng.float(0, 0.3);
    const t1 = rng.float(0.6, 1);
    neonLine(g, ax + (bx - ax) * t0, ay + (by - ay) * t0, ax + (bx - ax) * t1, ay + (by - ay) * t1, neon, neonAlpha, 2);
  }
}

/** Stars / motes in a band. */
function specks(g: G, rng: Rng, n: number, y0: number, y1: number, color: number, aMin: number, aMax: number, size = 1.6): void {
  for (let i = 0; i < n; i++) {
    const x = rng.float(0, W);
    const y = y0 + Math.pow(rng.next(), 1.6) * (y1 - y0);
    const a = rng.float(aMin, aMax);
    const s = rng.chance(0.08) ? size * 1.6 : size;
    fill(g, color, a);
    g.fillRect(x, y, s, s);
    if (s > size) softDot(g, x + s / 2, y + s / 2, s * 3, color, a * 0.4);
  }
}

/** Long soft cloud band (wraps). */
function cloudBand(g: G, rng: Rng, y: number, thickness: number, color: number, alpha: number, count: number): void {
  for (let i = 0; i < count; i++) {
    const x = rng.float(0, W);
    const rx = rng.float(140, 420);
    const ry = thickness * rng.float(0.5, 1.1);
    glowEllipse(g, x, y + rng.float(-thickness, thickness), rx, ry, color, alpha * rng.float(0.6, 1));
  }
}

// ───────────────────────────── Neon Graveyard ─────────────────────────────

const GY = {
  teal: 0x3fb8b0,
  pink: 0xc8508f,
  amber: 0xb89a6a,
};

const graveyard: Record<ParallaxLayer, Painter> = {
  sky(g, rng) {
    vgrad(g, 0, H, [
      [0, 0x060814],
      [0.4, 0x10153a],
      [HORIZON / H, 0x2b2a55],
      [1, 0x1a1a3a],
    ]);
    specks(g, rng, 160, 0, 520, 0xc9d3ef, 0.12, 0.5);
    // Veiled moon.
    glow(g, 1460, 250, 260, 0x8f93c8, 0.22);
    glow(g, 1460, 250, 110, 0xc9cde6, 0.35);
    g.beginPath();
    g.arc(1460, 250, 62, 0, TAU);
    const moon = g.createRadialGradient(1440, 232, 6, 1460, 250, 62);
    moon.addColorStop(0, rgba(0xe6e8f4, 0.95));
    moon.addColorStop(1, rgba(0xa9adcf, 0.9));
    g.fillStyle = moon;
    g.fill();
    // Craters (soft darker blots).
    for (const [x, y, r] of [
      [1446, 236, 12],
      [1478, 266, 9],
      [1466, 222, 6],
    ])
      softDot(g, x, y, r, 0x7f84a8, 0.35);
    // Veil clouds crossing the moon + high cloud bands.
    cloudBand(g, rng, 270, 26, 0x1a1d3e, 0.55, 10);
    cloudBand(g, rng, 420, 34, 0x3a3a6a, 0.16, 14);
    // Light pollution in the horizon haze.
    for (let i = 0; i < 7; i++) {
      glowEllipse(g, rng.float(0, W), HORIZON - 40, rng.float(220, 420), rng.float(80, 140), i % 2 ? GY.teal : GY.pink, 0.11);
    }
    cloudBand(g, rng, HORIZON - 80, 40, 0x4a4580, 0.12, 12);
    // Rain haze: very faint diagonal streak texture.
    g.save();
    g.globalAlpha = 0.05;
    g.strokeStyle = '#b8c4e8';
    g.lineWidth = 1;
    for (let i = 0; i < 500; i++) {
      const x = rng.float(0, W);
      const y = rng.float(0, H);
      const l = rng.float(20, 60);
      wrap(g, x - 10, x + 10, (dx) => {
        g.beginPath();
        g.moveTo(x + dx, y);
        g.lineTo(x + dx - l * 0.12, y + l);
        g.stroke();
      });
    }
    g.restore();
  },

  far(g, rng) {
    const crowns: Crown[] = ['flat', 'stepped', 'spire', 'slant', 'broken', 'stepped'];
    let x = 0;
    while (x < W) {
      const w = rng.float(60, 170);
      const top = rng.chance(0.25) ? rng.float(170, 300) : rng.float(320, 560);
      const col = mix(0x1d2140, 0x2a2f52, rng.next());
      tower(g, x, w, top, col, crowns[rng.int(0, crowns.length - 1)], rng.chance(0.4) ? rng.float(30, 110) : 0);
      windows(g, x, w, top + 10, HORIZON + 60, rng, [GY.teal, GY.amber, 0x8a9ad0], 0.05, 0.4, 3);
      if (rng.chance(0.35)) {
        // Faded vertical neon strip on the tower flank.
        const nx = x + (rng.chance(0.5) ? 5 : w - 7);
        neonLine(g, nx, top + 20, nx, top + rng.float(80, 200), rng.chance(0.5) ? GY.teal : GY.pink, 0.22, 2);
      }
      x += w + rng.float(-20, 40);
    }
    // Warning lights on the tallest masts.
    for (let i = 0; i < 9; i++) glow(g, rng.float(0, W), rng.float(150, 320), 8, 0xc8508f, 0.25);
    haze(g, 0x2b2a55, 300, HORIZON + 120, 0.15, 0.85);
  },

  mid(g, rng) {
    // Wet concrete blocks + broken billboards.
    let x = 0;
    while (x < W) {
      const w = rng.float(120, 260);
      const top = rng.float(430, 640);
      const col = mix(0x161c2e, 0x252e3e, rng.next());
      tower(g, x, w, top, col, (['flat', 'broken', 'stepped', 'slant'] as Crown[])[rng.int(0, 3)], rng.chance(0.3) ? rng.float(40, 90) : 0);
      // Cool wet rim on the lit (left) edge.
      rect(g, x, top, 2, H - top, 0x55627a, 0.35);
      windows(g, x, w, top + 12, H, rng, [GY.teal, GY.amber], 0.035, 0.35, 4);
      if (rng.chance(0.4)) {
        const bw = rng.float(100, 170);
        billboard(g, x + rng.float(0, w * 0.4), top - rng.float(130, 180), bw, bw * 0.45, 0x1a2030, 0x121726, rng.chance(0.5) ? GY.pink : GY.teal, rng, 0.45);
      }
      x += w + rng.float(10, 90);
    }
    // Elevated highway with pillars.
    const hy = 640;
    rect(g, 0, hy, W, 28, 0x1c2232);
    rect(g, 0, hy, W, 3, 0x56637a, 0.4);
    rect(g, 0, hy + 28, W, 6, 0x10141f);
    for (let px = 60; px < W; px += 256) {
      rect(g, px, hy + 34, 34, H - hy, 0x161b29);
      rect(g, px, hy + 34, 3, H - hy, 0x4b5670, 0.3);
    }
    // Highway lamps (faded).
    for (let px = 20; px < W; px += 128) glow(g, px, hy - 14, 18, GY.amber, 0.12);
    // Water tanks on stilts.
    for (let i = 0; i < 3; i++) {
      const tx = rng.float(0, W);
      const ty = rng.float(430, 520);
      rect(g, tx, ty, 70, 60, 0x1f2638);
      rect(g, tx + 6, ty + 60, 4, 70, 0x1a2030);
      rect(g, tx + 60, ty + 60, 4, 70, 0x1a2030);
      wrap(g, tx, tx + 70, (dx) => {
        fill(g, 0x1f2638);
        g.beginPath();
        g.ellipse(tx + 35 + dx, ty, 35, 12, 0, Math.PI, TAU);
        g.fill();
      });
    }
    haze(g, 0x2b2a55, 520, H, 0.05, 0.55);
  },

  near(g, rng) {
    // Industrial base: big pipes, gate frames, a crane and girders.
    let x = 0;
    while (x < W) {
      const w = rng.float(160, 340);
      const top = rng.float(700, 820);
      tower(g, x, w, top, mix(0x0d111d, 0x1a202c, rng.next()), 'flat');
      rect(g, x, top, 2, H - top, 0x3a465a, 0.35);
      x += w + rng.float(40, 160);
    }
    pipe(g, 0, 860, W, 16, 0x141a26, 0x55627a);
    pipe(g, 0, 920, W, 10, 0x111622, 0x4b5670);
    // Security gate frames (abstract) with faint teal lamps.
    for (let i = 0; i < 3; i++) {
      const gx = rng.float(0, W);
      rect(g, gx, 560, 18, H - 560, 0x121722);
      rect(g, gx + 180, 560, 18, H - 560, 0x121722);
      rect(g, gx - 10, 548, 218, 24, 0x161c2a);
      rect(g, gx - 10, 548, 218, 2, 0x55627a, 0.35);
      glow(g, gx + 9, 590, 10, GY.teal, 0.2);
      glow(g, gx + 189, 590, 10, GY.pink, 0.2);
    }
    // Antenna mast with guy wires.
    const mx = rng.float(200, W - 200);
    rect(g, mx, 260, 10, H - 260, 0x121722);
    lattice(g, mx + 5, 260, mx + 5, 780, 26, 0x121722, 3);
    cable(g, mx + 5, 300, mx - 260, 820, -40, 0x121722, 2);
    cable(g, mx + 5, 300, mx + 280, 820, -40, 0x121722, 2);
    glow(g, mx + 5, 256, 12, GY.pink, 0.35);
    haze(g, 0x1a1a3a, 600, H, 0.0, 0.35);
  },

  fore(g, rng) {
    const ink = 0x05070b;
    // Hanging cables across the top edge.
    for (let i = 0; i < 6; i++) {
      const x0 = rng.float(0, W);
      const span = rng.float(280, 620);
      cable(g, x0, -10, x0 + span, -10, rng.float(70, 150), ink, rng.float(3, 6));
    }
    // A hanging broken sign silhouette.
    const sx = rng.float(200, W - 200);
    rect(g, sx + 20, 0, 4, 60, ink);
    rect(g, sx + 110, 0, 4, 40, ink);
    wrap(g, sx, sx + 140, (dx) => {
      g.save();
      g.translate(sx + dx, 56);
      g.rotate(-0.12);
      fill(g, ink);
      g.fillRect(0, 0, 140, 52);
      g.restore();
    });
    neonLine(g, sx + 10, 70, sx + 80, 61, GY.pink, 0.18, 2);
    // Bottom: railings and rubble.
    rect(g, 0, H - 26, W, 26, ink);
    for (let x = 0; x < W; x += 64) rect(g, x, H - 70, 5, 50, ink);
    rect(g, 0, H - 72, W, 5, ink);
    for (let i = 0; i < 8; i++) {
      const rx = rng.float(0, W);
      const rw = rng.float(60, 180);
      const rh = rng.float(30, 90);
      wrap(g, rx, rx + rw, (dx) => {
        fill(g, ink);
        g.beginPath();
        g.moveTo(rx + dx, H);
        g.lineTo(rx + dx + rw * 0.2, H - rh);
        g.lineTo(rx + dx + rw * 0.55, H - rh * 0.7);
        g.lineTo(rx + dx + rw * 0.8, H - rh * 1.05);
        g.lineTo(rx + dx + rw, H);
        g.fill();
      });
    }
  },
};

// ───────────────────────────── Sky Foundry ─────────────────────────────

const FD = {
  molten: 0xd8642a,
  amber: 0xffb35a,
};

/** Floating assembly platform: deck + truss underside + faint thruster glow. */
function floatingPlatform(g: G, x: number, y: number, w: number, col: number, under: number, thruster: number, ta: number): void {
  wrap(g, x - 20, x + w + 20, (dx) => {
    const X = x + dx;
    fill(g, col);
    g.beginPath();
    g.moveTo(X, y);
    g.lineTo(X + w, y);
    g.lineTo(X + w * 0.85, y + w * 0.12);
    g.lineTo(X + w * 0.15, y + w * 0.12);
    g.closePath();
    g.fill();
    g.fillRect(X - 8, y - 6, w + 16, 8);
    fill(g, under);
    g.beginPath();
    g.moveTo(X + w * 0.3, y + w * 0.12);
    g.lineTo(X + w * 0.5, y + w * 0.22);
    g.lineTo(X + w * 0.7, y + w * 0.12);
    g.fill();
  });
  glow(g, x + w * 0.5, y + w * 0.24, w * 0.12, thruster, ta);
  glow(g, x + w * 0.22, y + w * 0.13, w * 0.07, thruster, ta * 0.7);
  glow(g, x + w * 0.78, y + w * 0.13, w * 0.07, thruster, ta * 0.7);
}

function chimney(g: G, x: number, w: number, top: number, col: number, smokeCol: number, rng: Rng, smokeA: number): void {
  rect(g, x, top, w, H - top, col);
  rect(g, x - 3, top, w + 6, 8, col);
  rect(g, x - 3, top + 30, w + 6, 4, col);
  // Smoke plume drifting right.
  for (let k = 0; k < 7; k++) {
    glow(g, x + w / 2 + k * 26 + rng.float(-6, 6), top - 20 - k * 22, 24 + k * 9, smokeCol, smokeA * (1 - k / 8));
  }
}

const foundry: Record<ParallaxLayer, Painter> = {
  sky(g, rng) {
    vgrad(g, 0, H, [
      [0, 0x1a1020],
      [0.35, 0x3a1a20],
      [0.58, 0x5a2a1e],
      [HORIZON / H, 0xc0562a],
      [0.8, 0xa04a26],
      [1, 0x5a2a1e],
    ]);
    // Muted sun behind the haze.
    glow(g, 760, HORIZON - 90, 420, 0xe07a3a, 0.28);
    glow(g, 760, HORIZON - 90, 150, 0xffc080, 0.4);
    g.beginPath();
    g.arc(760, HORIZON - 90, 70, 0, TAU);
    g.fillStyle = rgba(0xffd7a0, 0.55);
    g.fill();
    // Soot streaks.
    g.save();
    for (let i = 0; i < 9; i++) {
      const y = rng.float(80, 600);
      const x = rng.float(0, W);
      wrap(g, x - 500, x + 500, (dx) => {
        g.save();
        g.translate(x + dx, y);
        g.rotate(-0.08);
        softEllipse(g, 0, 0, rng.float(300, 520), rng.float(10, 26), 0x1a0e12, 0.35);
        g.restore();
      });
    }
    g.restore();
    cloudBand(g, rng, 520, 26, 0x7a3a2a, 0.22, 12);
    // Glowing cloud sea below the horizon.
    for (let row = 0; row < 5; row++) {
      const y = HORIZON + 30 + row * 60;
      for (let i = 0; i < 16; i++) {
        const x = (i / 16) * W + rng.float(-40, 40);
        const r = rng.float(70, 140);
        glowEllipse(g, x, y, r * 1.6, r * 0.55, mix(0xe08048, 0x6a2a1e, row / 5), 0.35);
      }
    }
    glowEllipse(g, 760, HORIZON + 10, 700, 40, 0xffb070, 0.3);
  },

  far(g, rng) {
    // Floating platforms + chimney stacks, sunlit and hazy.
    for (let i = 0; i < 7; i++) {
      const x = (i / 7) * W + rng.float(-60, 60);
      const w = rng.float(160, 300);
      const y = rng.float(320, 640);
      // Structures on the deck.
      for (let k = 0; k < 3; k++) {
        const bw = rng.float(20, 60);
        const bx = x + rng.float(0.1, 0.8) * w;
        const bh = rng.float(30, 120);
        rect(g, bx, y - bh, bw, bh, mix(0x3a2420, 0x5c3424, rng.next()));
      }
      floatingPlatform(g, x, y, w, 0x4a2c22, 0x3a2420, FD.molten, 0.18);
    }
    for (let i = 0; i < 4; i++) chimney(g, rng.float(0, W), rng.float(18, 34), rng.float(240, 420), 0x4a2c22, 0x8a5a4a, rng, 0.14);
    haze(g, 0xc0562a, 200, HORIZON + 100, 0.12, 0.8);
  },

  mid(g, rng) {
    // Assembly platforms with trusses, cranes and chains.
    for (let i = 0; i < 5; i++) {
      const x = (i / 5) * W + rng.float(-40, 40);
      const w = rng.float(280, 420);
      const y = rng.float(520, 700);
      // Truss legs down into the haze.
      lattice(g, x + w * 0.2, y, x + w * 0.2, H, 30, 0x241a17, 4);
      lattice(g, x + w * 0.8, y, x + w * 0.8, H, 30, 0x241a17, 4);
      // Deck + buildings.
      rect(g, x, y - 14, w, 22, 0x2a1e1a);
      rect(g, x, y - 14, w, 2, 0x6a4a3a, 0.4);
      for (let k = 0; k < 3; k++) {
        const bw = rng.float(40, 100);
        const bx = x + rng.float(0, w - bw);
        const bh = rng.float(40, 140);
        rect(g, bx, y - 14 - bh, bw, bh, mix(0x1e1614, 0x3a2a24, rng.next()));
        rect(g, bx, y - 14 - bh, 2, bh, 0x6a4a3a, 0.3);
        // Sparse sodium lamps.
        if (rng.chance(0.6)) glow(g, bx + bw * 0.5, y - 14 - bh * 0.5, 7, FD.amber, 0.35);
      }
      // Crane: mast + jib + chain + hook.
      if (i % 2 === 0) {
        const cx = x + w * 0.6;
        lattice(g, cx, y - 14, cx, y - 260, 18, 0x2a1e1a, 3);
        lattice(g, cx - 60, y - 250, cx + 220, y - 250, 16, 0x2a1e1a, 3);
        rect(g, cx + 170, y - 242, 2, 150, 0x241a17);
        rect(g, cx + 164, y - 94, 14, 10, 0x241a17);
      }
    }
    // Conveyor bridge.
    rect(g, 0, 600, W, 16, 0x261c18);
    for (let x = 0; x < W; x += 48) rect(g, x, 616, 4, 18, 0x221814);
    rect(g, 0, 600, W, 2, 0x6a4a3a, 0.3);
    haze(g, 0xa04a26, 500, H, 0.05, 0.6);
  },

  near(g, rng) {
    // Heavy trusses and smelter housings (dark; no bright orange near the playfield).
    let x = 0;
    while (x < W) {
      const w = rng.float(180, 360);
      const top = rng.float(720, 840);
      rect(g, x, top, w, H - top, mix(0x120c0c, 0x221816, rng.next()));
      rect(g, x, top, w, 3, 0x4a3226, 0.45);
      // Riveted bands.
      for (let ry = top + 30; ry < H; ry += 60) rect(g, x, ry, w, 3, 0x0c0808);
      x += w + rng.float(30, 140);
    }
    lattice(g, 0, 640, W, 640, 40, 0x1a1210, 5);
    for (let i = 0; i < 4; i++) {
      const lx = (i / 4) * W + rng.float(0, 200);
      lattice(g, lx, 660, lx, H, 34, 0x1a1210, 5);
    }
    // Smelter with a dim molten mouth.
    const sx = rng.float(300, W - 300);
    wrap(g, sx - 120, sx + 120, (dx) => {
      fill(g, 0x1c1311);
      g.beginPath();
      g.moveTo(sx + dx - 120, H);
      g.lineTo(sx + dx - 90, 700);
      g.lineTo(sx + dx + 90, 700);
      g.lineTo(sx + dx + 120, H);
      g.fill();
    });
    rect(g, sx - 40, 790, 80, 18, 0x3a1a10);
    glow(g, sx, 800, 60, FD.molten, 0.16);
    // Hanging chains.
    for (let i = 0; i < 5; i++) {
      const cx = rng.float(0, W);
      const len = rng.float(200, 420);
      for (let y = 640; y < 640 + len; y += 10) rect(g, cx - 2, y, 4, 7, 0x140e0c);
      rect(g, cx - 8, 640 + len, 16, 12, 0x140e0c);
    }
    haze(g, 0x5a2a1e, 600, H, 0.0, 0.3);
  },

  fore(g, rng) {
    const ink = 0x070506;
    // Top: overhead truss beam segments + chains.
    for (let i = 0; i < 3; i++) {
      const x = rng.float(0, W);
      const len = rng.float(380, 640);
      lattice(g, x, 24, x + len, 24, 44, ink, 7);
      rect(g, x, 0, len, 6, ink);
      for (let k = 0; k < 2; k++) {
        const cx = x + rng.float(40, len - 40);
        const l = rng.float(60, 150);
        for (let y = 46; y < 46 + l; y += 12) rect(g, cx - 3, y, 6, 9, ink);
        wrap(g, cx - 12, cx + 12, (dx) => {
          g.strokeStyle = rgba(ink);
          g.lineWidth = 6;
          g.beginPath();
          g.arc(cx + dx, 46 + l + 10, 10, -Math.PI * 0.1, Math.PI * 1.1, false);
          g.stroke();
        });
      }
    }
    // Bottom: railing + slag heaps.
    rect(g, 0, H - 30, W, 30, ink);
    rect(g, 0, H - 80, W, 6, ink);
    for (let x = 0; x < W; x += 80) rect(g, x, H - 80, 6, 52, ink);
    for (let i = 0; i < 7; i++) {
      const rx = rng.float(0, W);
      const rw = rng.float(80, 220);
      const rh = rng.float(40, 110);
      wrap(g, rx, rx + rw, (dx) => {
        fill(g, ink);
        g.beginPath();
        g.moveTo(rx + dx, H);
        g.quadraticCurveTo(rx + dx + rw * 0.5, H - rh * 2, rx + dx + rw, H);
        g.fill();
      });
    }
  },
};

// ───────────────────────────── Singularity Vault ─────────────────────────────

const VT = {
  cyan: 0x2fb8c8,
  violet: 0x8a5ae0,
};

/** Reactor ring facing the camera: thick ring with pylons and light slots. */
function reactorRing(g: G, x: number, y: number, r: number, thick: number, col: number, light: number, la: number): void {
  wrap(g, x - r - thick, x + r + thick, (dx) => {
    g.beginPath();
    g.arc(x + dx, y, r, 0, TAU);
    g.strokeStyle = rgba(col);
    g.lineWidth = thick;
    g.stroke();
    g.beginPath();
    g.arc(x + dx, y, r, 0, TAU);
    g.strokeStyle = rgba(light, la);
    g.lineWidth = 2;
    g.stroke();
    // Segment clamps.
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      g.save();
      g.translate(x + dx + Math.cos(a) * r, y + Math.sin(a) * r);
      g.rotate(a);
      g.fillStyle = rgba(col);
      g.fillRect(-thick * 0.8, -thick * 0.35, thick * 1.6, thick * 0.7);
      g.restore();
    }
  });
}

function pylon(g: G, x: number, w: number, top: number, col: number, light: number, la: number): void {
  wrap(g, x - 4, x + w + 4, (dx) => {
    const X = x + dx;
    fill(g, col);
    g.beginPath();
    g.moveTo(X, H);
    g.lineTo(X + w * 0.2, top);
    g.lineTo(X + w * 0.8, top);
    g.lineTo(X + w, H);
    g.fill();
    // Ribs.
    for (let y = top + 20; y < H; y += 26) g.fillRect(X + w * 0.1, y, w * 0.8, 4);
  });
  neonLine(g, x + w * 0.5, top + 12, x + w * 0.5, top + (H - top) * 0.4, light, la, 2);
}

const vault: Record<ParallaxLayer, Painter> = {
  sky(g, rng) {
    vgrad(g, 0, H, [
      [0, 0x020608],
      [0.4, 0x06161b],
      [HORIZON / H, 0x0d2c33],
      [1, 0x071a20],
    ]);
    // Faint distant ring of light.
    for (const [w, a] of [
      [40, 0.04],
      [14, 0.08],
      [3, 0.22],
    ]) {
      g.beginPath();
      g.ellipse(1200, 420, 520, 520, 0, 0, TAU);
      g.strokeStyle = rgba(VT.cyan, a);
      g.lineWidth = w;
      g.stroke();
    }
    glow(g, 1200, 420, 220, VT.violet, 0.1);
    // Energy veils: tall wavy curtains.
    for (let i = 0; i < 9; i++) {
      const x = rng.float(0, W);
      const col = i % 3 === 0 ? VT.violet : VT.cyan;
      for (let k = 0; k < 6; k++) {
        glowEllipse(g, x + Math.sin(k * 0.9 + i) * 30, 180 + k * 90, 46, 120, col, 0.035);
      }
    }
    specks(g, rng, 220, 0, H, 0x9fe8f0, 0.08, 0.4, 1.4);
    glowEllipse(g, W / 2, HORIZON, 1100, 90, VT.cyan, 0.08);
  },

  far(g, rng) {
    // Monoliths + giant reactor rings, hazy teal.
    reactorRing(g, 520, 470, 230, 34, 0x10242b, VT.cyan, 0.3);
    reactorRing(g, 1560, 520, 170, 26, 0x112830, VT.violet, 0.25);
    let x = 0;
    while (x < W) {
      const w = rng.float(50, 130);
      const top = rng.float(380, 640);
      tower(g, x, w, top, mix(0x0c1c22, 0x143038, rng.next()), (['flat', 'spire', 'stepped', 'dome'] as Crown[])[rng.int(0, 3)], rng.chance(0.25) ? rng.float(40, 120) : 0);
      if (rng.chance(0.5)) neonLine(g, x + w / 2, top + 16, x + w / 2, top + 60, VT.cyan, 0.3, 2);
      x += w + rng.float(20, 120);
    }
    haze(g, 0x0d2c33, 300, HORIZON + 100, 0.1, 0.8);
  },

  mid(g, rng) {
    // Containment pylons, coolant tanks, floating fragments.
    for (let i = 0; i < 6; i++) pylon(g, rng.float(0, W), rng.float(60, 110), rng.float(420, 600), mix(0x0b1418, 0x1c2c33, rng.next()), VT.cyan, 0.35);
    for (let i = 0; i < 4; i++) {
      const x = rng.float(0, W);
      const w = rng.float(90, 150);
      const top = rng.float(600, 720);
      rect(g, x, top, w, H - top, 0x132228);
      wrap(g, x, x + w, (dx) => {
        fill(g, 0x132228);
        g.beginPath();
        g.ellipse(x + dx + w / 2, top, w / 2, 16, 0, Math.PI, TAU);
        g.fill();
      });
      for (let y = top + 30; y < H; y += 50) rect(g, x, y, w, 4, 0x0b1418);
      rect(g, x + 4, top + 10, 2, H - top, 0x3a6a72, 0.3);
    }
    // Floating fragments held by gravity (violet underglow).
    for (let i = 0; i < 9; i++) {
      const fx = rng.float(0, W);
      const fy = rng.float(260, 560);
      const s = rng.float(18, 50);
      glow(g, fx, fy + s * 0.7, s * 1.3, VT.violet, 0.12);
      wrap(g, fx - s, fx + s, (dx) => {
        fill(g, 0x16262d);
        g.beginPath();
        g.moveTo(fx + dx - s, fy);
        g.lineTo(fx + dx - s * 0.3, fy - s * 0.5);
        g.lineTo(fx + dx + s * 0.8, fy - s * 0.3);
        g.lineTo(fx + dx + s, fy + s * 0.2);
        g.lineTo(fx + dx + s * 0.1, fy + s * 0.8);
        g.closePath();
        g.fill();
      });
    }
    haze(g, 0x0d2c33, 480, H, 0.05, 0.55);
  },

  near(g, rng) {
    // Magnetic coils (stacked rings), big pipes, ribbed towers; violet anomalies.
    let x = 0;
    while (x < W) {
      const w = rng.float(160, 320);
      const top = rng.float(720, 830);
      rect(g, x, top, w, H - top, mix(0x070c10, 0x14212a, rng.next()));
      rect(g, x, top, w, 2, 0x2f5a62, 0.4);
      x += w + rng.float(40, 160);
    }
    for (let i = 0; i < 3; i++) {
      const cx = rng.float(0, W);
      const top = rng.float(380, 520);
      rect(g, cx - 14, top, 28, H - top, 0x0c1418);
      for (let y = top + 10; y < 760; y += 34) {
        wrap(g, cx - 70, cx + 70, (dx) => {
          fill(g, 0x101c22);
          g.beginPath();
          g.ellipse(cx + dx, y, 64, 12, 0, 0, TAU);
          g.fill();
        });
        neonLine(g, cx - 54, y + 2, cx + 54, y + 2, VT.cyan, 0.12, 1.5);
      }
    }
    pipe(g, 0, 880, W, 18, 0x0c151a, 0x2f5a62);
    // Anomalies.
    for (let i = 0; i < 5; i++) {
      const ax = rng.float(0, W);
      const ay = rng.float(520, 820);
      glow(g, ax, ay, rng.float(30, 60), VT.violet, 0.14);
      glow(g, ax, ay, 8, 0xd0b8ff, 0.2);
    }
    haze(g, 0x071a20, 600, H, 0, 0.3);
  },

  fore(g, rng) {
    const ink = 0x030507;
    // Top: hanging conduits + small floating debris.
    for (let i = 0; i < 5; i++) {
      const x0 = rng.float(0, W);
      cable(g, x0, -8, x0 + rng.float(300, 560), -8, rng.float(60, 130), ink, rng.float(5, 9));
    }
    for (let i = 0; i < 6; i++) {
      const fx = rng.float(0, W);
      const fy = rng.float(30, 150);
      const s = rng.float(10, 26);
      wrap(g, fx - s, fx + s, (dx) => {
        fill(g, ink);
        g.beginPath();
        g.moveTo(fx + dx - s, fy);
        g.lineTo(fx + dx, fy - s * 0.6);
        g.lineTo(fx + dx + s, fy + s * 0.1);
        g.lineTo(fx + dx + s * 0.2, fy + s * 0.7);
        g.closePath();
        g.fill();
      });
    }
    // Bottom: pylon tips + coil silhouettes.
    rect(g, 0, H - 28, W, 28, ink);
    for (let i = 0; i < 6; i++) {
      const px = rng.float(0, W);
      const w = rng.float(40, 90);
      const h = rng.float(60, 130);
      wrap(g, px, px + w, (dx) => {
        fill(g, ink);
        g.beginPath();
        g.moveTo(px + dx, H);
        g.lineTo(px + dx + w * 0.25, H - h);
        g.lineTo(px + dx + w * 0.75, H - h);
        g.lineTo(px + dx + w, H);
        g.fill();
      });
    }
  },
};

const PAINTERS: Record<BiomeId, Record<ParallaxLayer, Painter>> = { graveyard, foundry, vault };
